/**
 * Generate a random string with specified length and character set.
 *
 * @param options - Generation options
 * @param options.length - Length of the random string (default: 4)
 * @param options.characters - Character set to use (default: alphanumeric uppercase)
 * @param options.prefix - Optional prefix to prepend
 * @param options.suffix - Optional suffix to append
 *
 * @returns Random string
 *
 * @example
 * generateRandomString() // 'A3K9'
 * generateRandomString({ length: 6 }) // 'X7M2P1'
 * generateRandomString({ length: 8, characters: '0123456789' }) // '84729103'
 * generateRandomString({ length: 4, prefix: 'PIN-' }) // 'PIN-A3K9'
 * generateRandomString({ length: 4, characters: 'ABCDEF', suffix: '-END' }) // 'ACBE-END'
 */
export function generateRandomString(
  options: {
    length?: number
    characters?: string
    prefix?: string
    suffix?: string
  } = {},
): string {
  const { length = 4, characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789', prefix = '', suffix = '' } = options

  if (length < 0) {
    throw new Error('Length must be non-negative')
  }

  if (characters.length === 0) {
    throw new Error('Character set cannot be empty')
  }

  let result = ''
  for (let i = 0; i < length; i++) {
    const randomIndex = Math.floor(Math.random() * characters.length)
    result += characters[randomIndex]
  }

  return prefix + result + suffix
}

/**
 * Preset character sets for common use cases.
 */
export const CharacterSets = {
  ALPHANUMERIC_UPPER: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789',
  ALPHANUMERIC_LOWER: 'abcdefghijklmnopqrstuvwxyz0123456789',
  ALPHANUMERIC_MIXED: 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789',
  NUMERIC: '0123456789',
  ALPHA_UPPER: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ',
  ALPHA_LOWER: 'abcdefghijklmnopqrstuvwxyz',
  ALPHA_MIXED: 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz',
  HEX_UPPER: '0123456789ABCDEF',
  HEX_LOWER: '0123456789abcdef',
  URL_SAFE: 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_',
} as const

/**
 * Generate a random numeric PIN.
 *
 * @example
 * generateRandomPin() // '7392'
 * generateRandomPin(6) // '847291'
 */
export function generateRandomPin(length = 4): string {
  return generateRandomString({ length, characters: CharacterSets.NUMERIC })
}

/**
 * Generate a random alphanumeric code (uppercase).
 *
 * @example
 * generateRandomCode() // 'A3K9'
 * generateRandomCode(8) // 'X7M2P1K5'
 */
export function generateRandomCode(length = 4): string {
  return generateRandomString({ length, characters: CharacterSets.ALPHANUMERIC_UPPER })
}

/**
 * Generate a random hex string.
 *
 * @example
 * generateRandomHex() // '7a4f'
 * generateRandomHex(8) // '3e8f92a1'
 */
export function generateRandomHex(length = 4, uppercase = false): string {
  return generateRandomString({
    length,
    characters: uppercase ? CharacterSets.HEX_UPPER : CharacterSets.HEX_LOWER,
  })
}
